// Data-fetching seam for the Trace Source panel. Kept separate from
// domain/hooks.ts (rather than adding it there) to avoid a circular import —
// domain/trace.ts already imports assuranceOf from domain/hooks.ts, so
// domain/hooks.ts importing back from domain/trace.ts would create a cycle.

import { useQuery } from "@tanstack/react-query";
import { useSeed } from "@/domain/hooks";
import { demoModeActive } from "@/domain/api";
import {
  resolveTrace,
  resolveTraceAsync,
  resolveAncestry,
  resolveAncestryAsync,
  type TraceTarget,
  type TraceResult,
  type TraceCrumb,
} from "@/domain/trace";

/**
 * Resolves the current node's full detail (up/down/related/evidence/
 * siblings). Demo mode reads synchronously from the seed blob (resolveTrace)
 * — wrapped in a resolved-immediately query so callers don't need two code
 * paths for loading state. Real mode calls the /api/trace/* endpoints
 * (resolveTraceAsync).
 */
export function useTraceNode(target: TraceTarget | undefined) {
  const seed = useSeed();
  const demo = demoModeActive();
  return useQuery<TraceResult | null>({
    queryKey: ["trace-node", target?.kind, target?.id, demo],
    queryFn: () => (demo ? Promise.resolve(target ? resolveTrace(seed, target) : null) : resolveTraceAsync(target!)),
    enabled: !!target,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Resolves the full ancestry chain (targets + labels) for a freshly-opened
 * trace target — the crumb labels seed the ribbon's label cache immediately,
 * so ancestor nodes don't need their own individual fetch just to render a
 * name in the ribbon (only the current node's full detail is fetched via
 * useTraceNode; the path's earlier nodes are cheap to have already visited
 * once by definition, but the seed labels here cover them before that walk
 * happens the first time).
 */
export async function resolveOpenAncestry(seed: ReturnType<typeof useSeed>, target: TraceTarget): Promise<TraceCrumb[]> {
  const crumbs = demoModeActive() ? resolveAncestry(seed, target) : await resolveAncestryAsync(target);
  return crumbs.length ? crumbs : [{ label: "…", target }];
}
